export type ArticleCardVariant =
  | "small" // image, title, date
  | "medium" // image, title, date, section/content access
  | "2md" // image, title, date, section, content access
  | "large" // image, title, date, section/content access/summary
  | "xl" // image, title, date, section, content access, summary after title
  | "xxl" // image, title, date, section/content access, summary after title
  | "featured"; // hero image, title, date, section/summary

export type ArticleCardElementsOrientation =
  | "vertical" // horizontal package
  | "vertical-reversed" // mata-mata stories listing
  | "vertical-summary" // mata-mata landing
  | "horizontal" // vertical package, section listing
  | "horizontal-summary"; // tags page, author page, search page

export type ArticleCardElementProps = {
  hasHeader?: boolean;
  hasIcon?: boolean;
  hasNewIcon?: boolean;
  hasImage?: boolean;
  hasParagraph?: boolean;
  hasSectionName?: boolean;
  hasTitle?: boolean;
  dateVariant?: "dash" | "or";
  hasTags?: boolean;
  hasDateSideBySide?: boolean;
  hasParaWithTitle?: boolean;
};

export type ArticleCardStyleProps = {
  cardWrapper?: string;
  contentWrapper?: string;
  headerWrapper?: string;
  hero?: string;
  description?: string;
  dateStyle?: string;
  titleStyle?: string;
  titleHoverStyle?: string;
  imageStyle?: string;
  tagWrapper?: string;
  sectionNameWrapper?: string;
  sectionNameItem?: string;
  iconStyle?: string;
  sectionNameStyle?: string;
  dateTitleWrapper?: string;
  titleParaWrapper?: string;
};

const DEFAULT_ELEMENTS: ArticleCardElementProps = {
  hasTitle: true,
  hasImage: true,
  hasParaWithTitle: false,
};

// --- Base Element Configurations ---

const VERTICAL_BASE_ELEMENTS: ArticleCardElementProps = {
  ...DEFAULT_ELEMENTS,
  hasNewIcon: true,
  hasSectionName: true,
};

const VERTICAL_REVERSED_BASE_ELEMENTS: ArticleCardElementProps = {
  ...DEFAULT_ELEMENTS,
};

const VERTICAL_SUMMARY_BASE_ELEMENTS: ArticleCardElementProps = {
  ...DEFAULT_ELEMENTS,
  hasParagraph: true,
  dateVariant: "dash",
};

const HORIZONTAL_BASE_ELEMENTS: ArticleCardElementProps = {
  ...DEFAULT_ELEMENTS,
  hasNewIcon: true,
  hasSectionName: true,
  hasParagraph: false,
};

const HORIZONTAL_SUMMARY_BASE_ELEMENTS: ArticleCardElementProps = {
  ...DEFAULT_ELEMENTS,
  hasSectionName: true,
  hasParagraph: true,
  hasDateSideBySide: true,
};

// --- The Elements Configuration ---

export const ARTICLE_CARD_ELEMENTS: Record<
  ArticleCardElementsOrientation,
  Record<ArticleCardVariant, { elements: ArticleCardElementProps }>
> = {
  vertical: {
    small: { elements: { ...VERTICAL_BASE_ELEMENTS } },
    medium: { elements: { ...VERTICAL_BASE_ELEMENTS, hasNewIcon: false } },
    "2md": { elements: {} },
    large: { elements: { ...VERTICAL_BASE_ELEMENTS, hasSectionName: false } },
    xl: { elements: {} },
    xxl: { elements: { ...VERTICAL_BASE_ELEMENTS } }, // Used in myBH starting layout
    featured: { elements: { ...VERTICAL_BASE_ELEMENTS } },
  },
  "vertical-reversed": {
    small: { elements: { ...VERTICAL_REVERSED_BASE_ELEMENTS } },
    medium: { elements: {} },
    "2md": { elements: {} },
    large: { elements: {} },
    xl: { elements: {} },
    xxl: { elements: {} },
    featured: { elements: {} },
  },
  "vertical-summary": {
    small: { elements: { ...VERTICAL_SUMMARY_BASE_ELEMENTS } },
    medium: { elements: { ...VERTICAL_SUMMARY_BASE_ELEMENTS } },
    "2md": { elements: {} },
    large: { elements: {} },
    xl: { elements: {} },
    xxl: {
      elements: {
        ...VERTICAL_BASE_ELEMENTS,
        hasParaWithTitle: true,
      },
    }, // used in myBH starting layout
    featured: { elements: {} },
  },
  horizontal: {
    small: { elements: { ...HORIZONTAL_BASE_ELEMENTS, hasSectionName: false } },
    medium: { elements: { ...HORIZONTAL_BASE_ELEMENTS, hasNewIcon: false } },
    "2md": { elements: { ...HORIZONTAL_BASE_ELEMENTS } },
    large: { elements: { ...HORIZONTAL_BASE_ELEMENTS, hasSectionName: false } },
    xl: {
      elements: {
        ...HORIZONTAL_BASE_ELEMENTS,
        hasNewIcon: false,
        hasParaWithTitle: true,
      },
    },
    xxl: {
      elements: {
        ...HORIZONTAL_BASE_ELEMENTS,
        hasSectionName: false,
        hasParaWithTitle: true,
      },
    },
    featured: {
      elements: { ...HORIZONTAL_BASE_ELEMENTS, hasParaWithTitle: true },
    },
  },
  "horizontal-summary": {
    small: {
      elements: {
        ...HORIZONTAL_BASE_ELEMENTS,
        hasNewIcon: false,
        hasSectionName: false,
      },
    },
    medium: {
      elements: { ...HORIZONTAL_SUMMARY_BASE_ELEMENTS, hasSectionName: false },
    },
    "2md": { elements: { ...HORIZONTAL_SUMMARY_BASE_ELEMENTS } },
    large: { elements: { ...HORIZONTAL_SUMMARY_BASE_ELEMENTS, hasIcon: true } },
    xl: { elements: {} },
    xxl: { elements: {} },
    featured: { elements: {} },
  },
};
